import type { ReactNode } from 'react';
import { AppProvider, useApp, useLoad } from './state';
import { FeedbackProvider } from './ui/feedback';
import { useRoute, type Route } from './router';
import { Empty } from './ui/bits';
import { Icon } from './ui/icons';
import { HumanAvatar } from './ui/avatar';
import { Tour, useTour } from './ui/tour';
import { ChatDrawer, useChatDrawer } from './ui/chat';
import { api } from './api';
import { Today } from './screens/Today';
import { CampaignList, CampaignDetail } from './screens/Campaigns';
import { CampaignSetup } from './screens/CampaignSetup';
import { Offers } from './screens/Offers';
import { TrackDetail } from './screens/Track';
import { Schedule } from './screens/Schedule';
import { CardexList, CardexDetail } from './screens/Cardex';
import { People } from './screens/People';
import { Vocab } from './screens/Vocab';
import { Audit } from './screens/Audit';
import { Integrations } from './screens/Integrations';
import { Web } from './screens/Web';
import { WorkItemPage, QueuePage } from './screens/Queue';
import { Pipeline, PlayDetail } from './screens/Pipeline';
import { Agents } from './screens/Agents';
import { Segments, SegmentDetail } from './screens/Segments';
import { Mailboxes } from './screens/Mailboxes';
import { Pages, PageDetail } from './screens/Pages';
import { Insights } from './screens/Insights';
import { Library, LibraryDetail } from './screens/Library';
import { Team } from './screens/Team';
import { Activity } from './screens/Activity';
import { Decisions } from './screens/Decisions';

// The shell. Desktop: a dark rail with grouped sections. Phone: the rail collapses to a brand strip and five bottom
// tabs (My day, Queue, Team, Pipeline, More); the rest of the sections live under More.
type NavItem = [key: string, label: string, icon: string, minRole?: string];
const GROUPS: Array<[string | null, NavItem[]]> = [
  [null, [['', 'My day', 'day'], ['queue', 'Queue', 'queue'], ['team', 'Team', 'team']]],
  ['Oversight', [['activity', 'Activity', 'activity'], ['decisions', 'Decisions', 'decisions'], ['insights', 'Strategic insights', 'insights']]],
  ['Work', [['pipeline', 'Pipeline', 'pipeline'], ['campaigns', 'Campaigns', 'campaigns'], ['segments', 'Segments', 'segments'], ['mailboxes', 'Mailboxes', 'mailboxes'], ['schedule', 'Schedule', 'schedule']]],
  ['Records', [['offers', 'Offers', 'offers'], ['library', 'Library', 'library'], ['pages', 'Pages', 'pages'], ['web', 'Web', 'web'], ['cardex', 'Cardex', 'cardex'], ['people', 'People', 'people'], ['vocab', 'Vocabularies', 'vocab']]],
  ['Admin', [['agents', 'Agent tuning', 'agents', 'manager'], ['integrations', 'Integrations', 'integrations'], ['audit', 'Audit', 'audit']]],
];
const TABS: NavItem[] = [['', 'My day', 'day'], ['queue', 'Queue', 'queue'], ['team', 'Team', 'team'], ['pipeline', 'Pipeline', 'pipeline'], ['more', 'More', 'more']];
const ALIAS: Record<string, string> = { work: 'queue', plays: 'pipeline', tracks: 'campaigns' };

function screen(r: Route): ReactNode {
  const [top, id] = r.parts;
  switch (top ?? '') {
    case '': return <Today />;
    case 'queue': return <QueuePage />;
    case 'team': return <Team />;
    case 'activity': return <Activity query={r.query} />;
    case 'decisions': return <Decisions query={r.query} />;
    case 'more': return <More />;
    case 'campaigns': return id === 'new' ? <CampaignSetup key="new" /> : id && r.parts[2] === 'edit' ? <CampaignSetup key={`edit-${id}`} mid={id} /> : id ? <CampaignDetail key={id} mid={id} /> : <CampaignList query={r.query} />;
    case 'offers': return <Offers />;
    case 'tracks': return id ? <TrackDetail key={id} tid={id} /> : null;
    case 'work': return id ? <WorkItemPage key={id} id={id} /> : <QueuePage />;
    case 'pipeline': return <Pipeline query={r.query} />;
    case 'plays': return id ? <PlayDetail key={id} id={id} /> : <Pipeline query={r.query} />;
    case 'segments': return id ? <SegmentDetail key={id} id={id} /> : <Segments />;
    case 'mailboxes': return <Mailboxes />;
    case 'pages': return id ? <PageDetail key={id} id={id} /> : <Pages />;
    case 'insights': return <Insights />;
    case 'library': return id ? <LibraryDetail key={id} id={id} /> : <Library />;
    case 'agents': return <Agents />;
    case 'schedule': return <Schedule query={r.query} />;
    case 'cardex': return id ? <CardexDetail key={id} tag={id} /> : <CardexList query={r.query} />;
    case 'people': return <People />;
    case 'vocab': return <Vocab key={id || 'channel'} name={id || 'channel'} />;
    case 'audit': return <Audit query={r.query} />;
    case 'integrations': return <Integrations />;
    case 'web': return <Web query={r.query} />;
    default: return null;
  }
}

/** The phone's fifth tab: every other section as a list. */
function More() {
  const { can } = useApp();
  return (<>
    <div className="page-head"><div><h1>More</h1><div className="sub">Everything that is not on a tab</div></div></div>
    <div className="card">
      {GROUPS.slice(1).flatMap(([, items]) => items).filter(([, , , role]) => !role || can(role)).map(([k, label, icon]) => (
        <a key={k} href={`#/${k}`} className="row" style={{ padding: '14px 16px', borderTop: '1px solid var(--rule-soft)', minHeight: 52 }}><Icon name={icon} className="ico" /><span className="grow"><b style={{ fontWeight: 500 }}>{label}</b></span><Icon name="back" className="ico" /></a>
      ))}
    </div>
  </>);
}

function Me() {
  const { me } = useApp();
  return (
    <div className="me">
      <HumanAvatar name={me.user.display_name} size="s" />
      <span className="who"><strong>{me.user.display_name}</strong>{me.user.role}{me.party ? ` · ${me.party.name}` : ''}</span>
    </div>
  );
}

function Rail({ top, signedIn }: { top: string; signedIn: boolean }) {
  const { can, me } = useApp();
  const counts = useLoad(() => api('GET', '/work?assignee=me&limit=200').then((r) => Object.values(r.counts as Record<string, number>).reduce((a, b) => a + b, 0)).catch(() => 0), [me.user.id]);
  const n = counts.data ?? 0;
  return (
    <nav className="rail" aria-label="Sections">
      <a className="brand" href="#/" aria-label="3HUE ECARMA home"><img className="wordmark" src="/brand/3hue-wordmark-dark.png?v=473aaf3f" alt="3HUE" /><span className="product"><b>ECARMA</b><small>Enterprise Customer Acquisition &amp; RevOps Management Application</small></span></a>
      {GROUPS.map(([grp, items]) => {
        const rows = items.filter(([, , , role]) => !role || can(role));
        if (!rows.length) return null;
        return (<div key={grp ?? 'top'} style={{ display: 'contents' }}>
          {grp && <div className="grp">{grp}</div>}
          {rows.map(([k, label, icon]) => <a key={k} href={`#/${k}`} data-tour={`rail-${k || 'day'}`} aria-current={k === top ? 'page' : undefined}><Icon name={icon} />{label}{k === 'queue' && n > 0 && <span className="count">{n}</span>}</a>)}
        </div>);
      })}
      <div className="spacer" />
      {signedIn ? <Me /> : <div className="me">Signing in…</div>}
    </nav>
  );
}

function TabBar({ top }: { top: string }) {
  const onTab = TABS.some(([k]) => k === top);
  return (
    <nav className="tabbar" aria-label="Sections">
      {TABS.map(([k, label, icon]) => <a key={k} href={`#/${k}`} aria-current={k === top || (k === 'more' && !onTab) ? 'page' : undefined}><Icon name={icon} />{label}</a>)}
    </nav>
  );
}

function Frame({ children, signedIn }: { children: ReactNode; signedIn: boolean }) {
  const route = useRoute();
  const raw = route.parts[0] ?? '';
  const top = ALIAS[raw] ?? raw;
  return (
    <div className="frame">
      {signedIn ? <Rail top={top} signedIn /> : (
        <nav className="rail" aria-label="Sections"><a className="brand" href="#/" aria-label="3HUE ECARMA home"><img className="wordmark" src="/brand/3hue-wordmark-dark.png?v=473aaf3f" alt="3HUE" /><span className="product"><b>ECARMA</b><small>Enterprise Customer Acquisition &amp; RevOps Management Application</small></span></a><div className="spacer" /><div className="me">Signing in…</div></nav>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <main aria-live="polite" style={{ flex: 1 }}>{children}</main>
        {signedIn && <TabBar top={top} />}
      </div>
    </div>
  );
}

function Routed() {
  const route = useRoute();
  const tour = useTour();
  const chat = useChatDrawer();
  return <Frame signedIn>{screen(route) ?? <Empty>No screen at {route.path}</Empty>}{tour.open && <Tour onClose={tour.close} role={tour.open.role} />}{chat.target && <ChatDrawer target={chat.target} onClose={chat.close} />}</Frame>;
}

export function App() {
  return (
    <FeedbackProvider>
      <AppProvider fallback={(err) => <Frame signedIn={false}>{err ? <Empty><strong>Can’t sign you in.</strong><br />{err}</Empty> : null}</Frame>}>
        <Routed />
      </AppProvider>
    </FeedbackProvider>
  );
}
